/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react-hooks/exhaustive-deps */
// app/components/Dentist.tsx
"use client";

import { useRef, useEffect } from "react";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useMotionValue,
  MotionValue,
} from "framer-motion";
import Image from "next/image";

const GREEN = "rgb(34 150 83)";

export default function Dentist() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });

  /* ---------------- PARALLAX ---------------- */
  const { scrollYProgress } = useScroll({
    target: rootRef,
    offset: ["start end", "end start"],
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 180]);

  /* ---------------- TILT (DESKTOP ONLY) ---------------- */
  const imageRef = useRef<HTMLDivElement>(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 1024) return; // ❗ Disable tilt on mobile & tablet

    const el = imageRef.current;
    if (!el) return;

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      rotateY.set((x - 0.5) * 14);
      rotateX.set((y - 0.5) * -14);
    };

    const onLeave = () => {
      rotateX.set(0);
      rotateY.set(0);
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);

    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [rotateX, rotateY]);

  return (
    <section
      ref={rootRef}
      id="dentist"
      className="relative overflow-hidden bg-gradient-to-b from-white via-emerald-50/20 to-white py-12 sm:py-16 lg:py-20"
    >
      {/* ================= BACKGROUND SHAPES ================= */}
      <div className="pointer-events-none absolute inset-0 opacity-20">
        <motion.div
          className="absolute left-[8%] top-32 hidden lg:block size-40 rounded-3xl bg-emerald-200"
          style={{
            rotate: rotate as unknown as MotionValue<number>,
            y: y1 as unknown as MotionValue<number>,
          }}
        />
        <motion.div
          className="absolute right-[10%] top-48 hidden lg:block size-32 rounded-full bg-cyan-200"
          animate={{ scale: [1, 1.2, 1], x: [0, 20, 0] }}
          transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <motion.span
            className="inline-block rounded-full border-2 border-emerald-300 bg-emerald-100 px-5 py-2 text-xs sm:text-sm font-bold text-emerald-700"
            whileHover={{ scale: 1.08 }}
          >
            👨‍⚕️ Meet Your Dentist
          </motion.span>

          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-6xl font-black text-gray-900">
          Best Dentist in {" "}<br></br>
            <span className="bg-gradient-to-r from-emerald-600 to-emerald-400 bg-clip-text text-transparent">
            RT Nagar 
            </span>
          </h2>
        </motion.div>

        {/* ================= CONTENT ================= */}
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-28">
          {/* ---------- IMAGE ---------- */}
          <motion.div
            ref={imageRef}
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative mx-auto w-full max-w-md sm:max-w-lg lg:max-w-none"
            style={{
              perspective: 1200,
              transformStyle: "preserve-3d",
            }}
          >
            <motion.div
              style={{
                rotateX,
                rotateY,
              }}
              className="relative"
            >
              <Image
                src="/dentist/ayush.png"
                alt="Dr. Ayush – Principal Dentist at Pinak Dental Clinic"
                width={800}
                height={650}
                priority
                className="
                  h-[360px]
                  sm:h-[420px]
                  lg:h-[550px]
                  w-full
                  object-cover
                   
                "
              />

              {/* Badge */}
              <div className="absolute right-4 top-4 rounded-xl border-2 border-white bg-white/95 px-3 py-2 text-center backdrop-blur">
                <p className="text-xl font-black text-emerald-600">BDS</p>
                <p className="text-[10px] text-gray-600">Certified</p>
              </div>
            </motion.div>
          </motion.div>

          {/* ---------- TEXT ---------- */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
            >
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900">
                Dr. Ayush
              </h3>
              <p className="mt-2 text-lg sm:text-xl font-semibold text-emerald-600">
                Principal Dentist – Pinak Dental Clinic
              </p>
            </motion.div>

            {[
              {
                text: (
                  <>
                    Dr. Ayush offers comprehensive expertise in advanced root canal treatments,
                    cosmetic and intraoral dental procedures, and painless restorative dentistry
                    as your{" "}
                    <strong>trusted dentist in RT Nagar. </strong>

                    Each treatment is delivered with a strong focus on precision, comfort, and
                    predictable, long-term results.
                  </>
                ),
                border: "border-emerald-200",
                bg: "from-emerald-50 to-white",
              },
              {
                text: `His clinical approach combines modern dental techniques with
                meticulous planning and execution.`,
                border: "border-blue-200",
                bg: "from-blue-50 to-white",
              },
              {
                text: `As a trusted dentist in RT Nagar, Dr. Ayush places a strong emphasis on patient-centric care, focusing on long-term oral health and natural-looking outcomes.`,
                border: "border-purple-200",
                bg: "from-purple-50 to-white",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className={`rounded-3xl border-2 ${item.border} bg-gradient-to-br ${item.bg} p-5 shadow-md`}
              >
                <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
